import {
	useForm,
	getFormProps,
	getInputProps,
	getSelectProps,
} from '@conform-to/react'
import { parseWithZod, getZodConstraint } from '@conform-to/zod/v4'
import { useState } from 'react'
import { data, Form, Link, useLoaderData } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import {
	Field,
	TextareaField,
	ErrorList,
	MultiSelectField,
	SelectField,
} from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { type SeoProps } from '#app/seo/seo.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server'
import { useIsPending } from '#app/utils/misc.tsx'
import { type Route } from './+types/index.ts'
import { submitBusiness } from './__submit.server.tsx'
import { getSubmitBusinessSchema } from './submit-schema.ts'

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	let user: { name: string | null; email: string } | null = null
	if (userId) {
		user = await prisma.user.findUnique({
			where: { id: userId },
			select: { name: true, email: true },
		})
	}

	// Get all categories, cities, and subcategories for the form
	const [categories, cities, subcategories] = await Promise.all([
		prisma.category.findMany({
			select: {
				id: true,
				name: true,
			},
			orderBy: {
				name: 'asc',
			},
		}),
		prisma.city.findMany({
			select: {
				id: true,
				name: true,
			},
			orderBy: {
				name: 'asc',
			},
		}),
		prisma.subcategory.findMany({
			select: {
				id: true,
				name: true,
				categoryId: true,
				category: { select: { name: true } },
			},
			orderBy: {
				name: 'asc',
			},
		}),
	])

	return data({ user, categories, cities, subcategories })
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const intent = formData.get('intent')

	if (intent === 'submit-business') {
		return submitBusiness({ request, formData })
	}

	return data({ error: 'Invalid intent' }, { status: 400 })
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb() {
		return { name: 'Submit Business', url: '/submit-business' }
	},
	seo({ canonical }: { canonical: string }): SeoProps {
		return {
			title: 'Submit Your Business - RentDFW.com',
			description:
				"Submit your business to the RentDFW directory. We'll review your submission and get back to you as soon as possible.",
			canonical,
			openGraph: {
				title: 'Submit Your Business - RentDFW.com',
				description:
					"Submit your business to the RentDFW directory. We'll review your submission and get back to you as soon as possible.",
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: 'Submit Your Business - RentDFW.com',
				description:
					"Submit your business to the RentDFW directory. We'll review your submission and get back to you as soon as possible.",
			},
		}
	},
}

export default function SubmitBusinessRoute({
	actionData,
}: Route.ComponentProps) {
	const { user, categories, cities, subcategories } =
		useLoaderData<typeof loader>()
	const isPending = useIsPending()
	const schema = getSubmitBusinessSchema({ isLoggedIn: Boolean(user) })

	// Type guard to safely extract lastResult
	const lastResult =
		actionData && typeof actionData === 'object' && 'lastResult' in actionData
			? actionData.lastResult
			: undefined

	const [form, fields] = useForm({
		id: 'submit-business-form',
		constraint: getZodConstraint(schema),
		lastResult,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema })
		},
		shouldRevalidate: 'onBlur',
	})

	const result =
		actionData && typeof actionData === 'object' && 'result' in actionData
			? (actionData.result as 'duplicate' | 'validation')
			: undefined
	const hadDuplicates = result === 'duplicate'
	const duplicatesList =
		actionData && typeof actionData === 'object' && 'duplicates' in actionData
			? (actionData.duplicates as Array<any>)
			: undefined

	const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([])

	const subcategoryOptions = subcategories
		.filter((s) => selectedCategoryIds.includes(s.categoryId))
		.map((s) => ({
			value: s.id,
			label: s.name,
			searchText: `${s.name} ${s.category?.name ?? ''}`,
		}))

	return (
		<div className="container mx-auto max-w-2xl pt-12 pb-24">
			<Card>
				<CardHeader>
					<CardTitle>Submit your business</CardTitle>
				</CardHeader>
				<CardContent>
					<Form method="post" {...getFormProps(form)}>
						<HoneypotInputs />
						<input type="hidden" name="intent" value="submit-business" />
						<Field
							labelProps={{ children: 'Business Name' }}
							inputProps={{
								...getInputProps(fields.name!, { type: 'text' }),
								placeholder: 'Local Rental Co',
							}}
							errors={fields.name?.errors}
						/>

						<Field
							labelProps={{ children: 'Street Address' }}
							inputProps={{
								...getInputProps(fields.addressStreet!, { type: 'text' }),
								placeholder: '123 Main St',
							}}
							errors={fields.addressStreet?.errors}
						/>

						<input
							type="hidden"
							name={fields.addressRegion!.name}
							value="Texas"
						/>
						<input
							type="hidden"
							name={fields.addressCountryCode!.name}
							value="US"
						/>

						<div className="grid grid-cols-2 gap-4">
							{cities.length > 0 ? (
								<SelectField
									labelProps={{ children: 'City' }}
									selectProps={{
										...getSelectProps(fields.addressCity!),
										placeholder: 'Select city...',
									}}
									selectOptions={cities.map((city) => ({
										value: city.name,
										label: city.name,
									}))}
									field={fields.addressCity!}
									errors={fields.addressCity?.errors}
								/>
							) : (
								<Field
									labelProps={{ children: 'City' }}
									inputProps={{
										...getInputProps(fields.addressCity!, { type: 'text' }),
										placeholder: 'Dallas',
									}}
									errors={fields.addressCity?.errors}
								/>
							)}

							<Field
								labelProps={{ children: 'Postal Code' }}
								inputProps={{
									...getInputProps(fields.addressZip!, { type: 'text' }),
									placeholder: '75201',
								}}
								errors={fields.addressZip?.errors}
							/>
						</div>

						<Field
							labelProps={{ children: 'Website ' }}
							inputProps={{
								...getInputProps(fields.website!, { type: 'url' }),
								placeholder: 'https://example.com',
							}}
							errors={fields.website?.errors}
						/>

						{categories.length > 0 && (
							<MultiSelectField
								className="mb-4"
								labelProps={{ children: 'Categories ' }}
								multiSelectProps={{
									name: fields.categoryIds!.name,
									placeholder: 'Select categories...',
									emptyText: 'No categories found',
								}}
								multiSelectOptions={categories.map((category) => ({
									label: category.name,
									value: category.id,
								}))}
								field={fields.categoryIds!}
								errors={fields.categoryIds?.errors}
								onValueChange={(options) =>
									setSelectedCategoryIds(options.map((o) => o.value))
								}
							/>
						)}

						{selectedCategoryIds.length > 0 && (
							<MultiSelectField
								className="mb-4"
								labelProps={{ children: 'Subcategories' }}
								multiSelectProps={{
									name: fields.subcategoryIds!.name,
									placeholder: 'Select subcategories...',
									emptyText: 'No subcategories found',
								}}
								multiSelectOptions={subcategoryOptions}
								field={fields.subcategoryIds!}
								errors={fields.subcategoryIds?.errors}
							/>
						)}

						{cities.length > 0 && (
							<MultiSelectField
								className="mb-4"
								labelProps={{ children: 'Cities Served' }}
								multiSelectProps={{
									name: fields.cityIds!.name,
									placeholder: 'Select cities...',
									emptyText: 'No cities found',
								}}
								multiSelectOptions={cities.map((city) => ({
									label: city.name,
									value: city.id,
								}))}
								field={fields.cityIds!}
								errors={fields.cityIds?.errors}
							/>
						)}

						<TextareaField
							labelProps={{ children: 'Note ' }}
							textareaProps={{
								...getInputProps(fields.note!, { type: 'text' }),
								placeholder: 'Anything else we should know?',
								rows: 3,
							}}
							errors={fields.note?.errors}
						/>

						{!user ? (
							<div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
								<Field
									labelProps={{ children: 'Your Name' }}
									inputProps={{
										...getInputProps(fields.contactName!, { type: 'text' }),
										placeholder: 'Jane Doe',
									}}
									errors={fields.contactName?.errors}
								/>
								<Field
									labelProps={{ children: 'Your Email' }}
									inputProps={{
										...getInputProps(fields.contactEmail!, { type: 'email' }),
										placeholder: 'you@example.com',
									}}
									errors={fields.contactEmail?.errors}
								/>
							</div>
						) : null}

						<ErrorList errors={form.errors} id={form.errorId} />

						<StatusButton
							type="submit"
							className="w-full"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							disabled={isPending}
						>
							Submit Listing
						</StatusButton>

						{hadDuplicates ? (
							<div className="mt-6 rounded-md border border-yellow-300 bg-yellow-50 p-4 text-sm">
								<div className="mb-2 flex items-center gap-2 font-medium text-yellow-900">
									<Icon name="info" />
									Possible duplicate listing found
								</div>
								<p className="text-yellow-900">
									We found an existing listing that may match. Please request a
									claim to manage it.
								</p>
								<ul className="mt-3 list-inside list-disc text-yellow-900">
									{duplicatesList?.map((b: any) => (
										<li key={b.id}>
											<strong>{b.name}</strong>
											{b.website ? ` — ${b.website}` : ''}
											{b.address ? ` — ${b.address}` : ''}
											<Button
												type="button"
												variant="link"
												size="sm"
												className="ml-2 text-yellow-900 underline"
												asChild
											>
												<Link to={`/claim?businessId=${b.id}`}>
													Claim this listing
												</Link>
											</Button>
										</li>
									))}
								</ul>
							</div>
						) : null}
					</Form>
				</CardContent>
			</Card>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
